/**
 * Mô tả sản phẩm song ngữ — tiếng Anh và tiếng Hoa.
 *
 * Khách chốt (13/09/2026, phương án A): sản phẩm giữ CẢ HAI bản mô tả cùng một
 * ô đánh dấu bản nào là chính; còn chọn in bằng tiếng nào thì nằm ở TỪNG CHỨNG
 * TỪ. Lý do không đặt cờ ngôn ngữ ở sản phẩm: cùng một mã nút bán cho cả khách
 * Trung Quốc lẫn khách châu Âu, một cờ trên sản phẩm sẽ ép mọi khách dùng chung
 * một thứ tiếng, và đổi cho khách này là đổi luôn cho khách kia.
 *
 * Ô "chính" trên sản phẩm chỉ quyết định hai việc: bản nào được chép vào dòng
 * hàng khi chưa biết chứng từ dùng tiếng gì, và bản nào in lên tem.
 */

export const DESCRIPTION_LANGS = ["en", "zh"] as const;
export type DescriptionLang = (typeof DESCRIPTION_LANGS)[number];

export function isDescriptionLang(value: unknown): value is DescriptionLang {
  return typeof value === "string" && (DESCRIPTION_LANGS as readonly string[]).includes(value);
}

/**
 * Bất cứ thứ gì mang hai bản mô tả: sản phẩm, hoặc một dòng hàng đã chụp lại.
 *
 * `description` là bản TIẾNG ANH. Giữ nguyên tên cột cũ thay vì đổi thành
 * `description_en`: 1.459 sản phẩm và năm bảng dòng hàng đang dùng tên này,
 * đổi tên chỉ để cho đẹp là rủi ro không đổi lấy lợi ích nào cho người dùng.
 */
export interface BilingualDescription {
  description?: string | null;
  descriptionZh?: string | null;
}

const clean = (value: string | null | undefined): string => (value ?? "").trim();

/**
 * Lấy bản mô tả để hiển thị hoặc in.
 *
 * Ô được chọn mà trống thì LÙI VỀ ô còn lại, không in trống. Báo giá gửi khách
 * mà cột mô tả rỗng thì tệ hơn hẳn việc hiện sai thứ tiếng — và người ta sẽ chỉ
 * phát hiện ra sau khi đã gửi đi.
 */
export function describe(source: BilingualDescription, lang: DescriptionLang): string {
  const primary = lang === "zh" ? clean(source.descriptionZh) : clean(source.description);
  if (primary) return primary;
  return lang === "zh" ? clean(source.description) : clean(source.descriptionZh);
}

/** Bản mô tả có thật ở ô đó không — để giao diện báo "ô này đang trống". */
export function hasDescription(source: BilingualDescription, lang: DescriptionLang): boolean {
  return !!clean(lang === "zh" ? source.descriptionZh : source.description);
}

/** Bản mô tả đang được in có phải là bản lùi về hay không. */
export function isFallback(source: BilingualDescription, lang: DescriptionLang): boolean {
  return !hasDescription(source, lang) && !!describe(source, lang);
}

/**
 * Ghi một bản mô tả vào đúng ô của thứ tiếng đang chọn.
 *
 * Bộ soạn dòng hàng chỉ có MỘT ô nhập: nó sửa bản của thứ tiếng chứng từ đang
 * dùng. Hai ô cạnh nhau trên một bảng vốn đã chật (mã, mô tả, màu, cỡ, đơn vị,
 * số lượng, đơn giá) thì không ai đọc nổi.
 */
export function withDescription<T extends BilingualDescription>(
  source: T,
  lang: DescriptionLang,
  value: string,
): T {
  return lang === "zh" ? { ...source, descriptionZh: value } : { ...source, description: value };
}
